import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, ChevronDown, Home, Mail, MessageCircle, User } from 'lucide-react';
import api from '../api/client';
import {
  DEFAULT_COUNTRY, NO_POSTCODE_COUNTRIES, countryName, countryOptions, dialCode, flagEmoji,
} from '../utils/countries';

//
// Early Access registration (MyDrAi Waitlist Add-On, 03.10.2026) — replaces
// the email-only waitlist on /preventive-health and /preventive-health-cn.
//
//   Step 1  name, email, country code + phone (WhatsApp preferred)
//   Step 2  country, zip / postcode, age group, interests, blood-test
//           timing, consent
//
// Global waitlist (client 2026-10-08): phone takes a mandatory country code;
// Zip / Postcode is optional for NO_POSTCODE_COUNTRIES, mandatory otherwise.
//   Step 3  full-screen confirmation popup → auto-return to homepage after
//           10s (client 2026-10-07), or immediately via the button.
//
// Option keys must match backend/src/models/WaitlistSignup.js.
//

const AGE_GROUPS = ['under_25', '25_34', '35_44', '45_54', '55_plus'];
const INTERESTS = [
  'general_health_check',
  'diabetes_blood_sugar',
  'cholesterol_heart',
  'liver',
  'kidney',
  'vitamins_nutrition',
  'weight_management',
  'overall_health',
  'other',
];
const TEST_TIMINGS = ['asap', '1_3_months', '3_6_months', 'exploring'];

const COPY = {
  en: {
    heading: 'Be First to Know. Be Part of the Movement.',
    step: (n) => `Step ${n} of 2`,
    firstName: 'Name',
    email: 'Email address',
    phone: 'Phone Number (WhatsApp preferred)',
    phonePh: 'e.g. 12-345 6789',
    phoneCode: 'Country code',
    next: 'Next',
    back: 'Back',
    step2Title: "Help us understand what you're looking for.",
    country: 'Country',
    popular: 'Popular',
    allCountries: 'All countries',
    postcode: 'Zip / Postcode',
    optional: '(optional)',
    postcodePh: (iso) => (iso === 'MY' ? 'e.g. 50450' : 'Zip / Postcode'),
    ageGroup: 'Age group',
    ageLabels: { under_25: 'Under 25', '25_34': '25–34', '35_44': '35–44', '45_54': '45–54', '55_plus': '55+' },
    interests: 'Why are you interested? What would you like to understand about your health?',
    interestsHint: 'Select all that apply.',
    interestLabels: {
      general_health_check: 'General health check',
      diabetes_blood_sugar: 'Diabetes / blood sugar',
      cholesterol_heart: 'Cholesterol / heart health',
      liver: 'Liver health',
      kidney: 'Kidney health',
      vitamins_nutrition: 'Vitamins & nutrition',
      weight_management: 'Weight management',
      overall_health: 'I want to understand my overall health',
      other: 'Other',
    },
    otherPh: 'Tell us more (optional)',
    timing: 'When are you likely to do your blood test?',
    timingLabels: {
      asap: 'As soon as available',
      '1_3_months': 'Within 1–3 months',
      '3_6_months': 'Within 3–6 months',
      exploring: 'Just exploring for now',
    },
    consent: 'I agree to receive updates, early-access offers and information about MyDrAi services via email and WhatsApp.',
    submit: 'Join Early Access',
    submitting: 'Joining…',
    thanksTitle: "Thank you! 🎉 You're on the MyDrAi Blood Test Early Access List.",
    thanksBody: "We'll reach out by email or WhatsApp as soon as it's available.",
    backHome: 'Back to homepage',
    redirecting: (n) => `Returning to the homepage in ${n}s…`,
    footer: 'No payment. No spam. Just updates when we\'re ready.',
    err: {
      firstName: 'Please enter your name.',
      email: 'Please enter a valid email address.',
      phone: 'Please enter a valid phone number.',
      country: 'Please select your country.',
      postcode: (iso) => (iso === 'MY' ? 'Please enter a valid 5-digit postcode.' : 'Please enter a valid zip / postcode.'),
      ageGroup: 'Please select your age group.',
      interests: 'Please select at least one area of interest.',
      timing: 'Please tell us when you are likely to do your blood test.',
      consent: 'Please tick the box to agree to receive updates.',
      generic: 'Could not join right now. Please try again.',
    },
  },
  zh: {
    heading: '优先获得通知，成为这股力量的一部分。',
    step: (n) => `第 ${n} 步，共 2 步`,
    firstName: '姓名',
    email: '电子邮箱',
    phone: '电话号码（建议填写 WhatsApp）',
    phonePh: '例如 12-345 6789',
    phoneCode: '国家/地区代码',
    next: '下一步',
    back: '返回',
    step2Title: '帮助我们了解您的需求。',
    country: '国家/地区',
    popular: '常用',
    allCountries: '所有国家/地区',
    postcode: '邮编 / 邮区编号',
    optional: '（选填）',
    postcodePh: (iso) => (iso === 'MY' ? '例如 50450' : '邮编 / 邮区编号'),
    ageGroup: '年龄组别',
    ageLabels: { under_25: '25 岁以下', '25_34': '25–34 岁', '35_44': '35–44 岁', '45_54': '45–54 岁', '55_plus': '55 岁及以上' },
    interests: '您为何感兴趣？您希望了解哪方面的健康状况？',
    interestsHint: '可多选。',
    interestLabels: {
      general_health_check: '一般健康检查',
      diabetes_blood_sugar: '糖尿病 / 血糖',
      cholesterol_heart: '胆固醇 / 心脏健康',
      liver: '肝脏健康',
      kidney: '肾脏健康',
      vitamins_nutrition: '维生素与营养',
      weight_management: '体重管理',
      overall_health: '我想了解自己的整体健康',
      other: '其他',
    },
    otherPh: '请简单说明（选填）',
    timing: '您预计何时进行验血？',
    timingLabels: {
      asap: '一推出就进行',
      '1_3_months': '1–3 个月内',
      '3_6_months': '3–6 个月内',
      exploring: '目前只是了解',
    },
    consent: '我同意通过电子邮件和 WhatsApp 接收 MyDrAi 服务的最新消息、早鸟优惠及相关信息。',
    submit: '加入早鸟名单',
    submitting: '加入中…',
    thanksTitle: '谢谢您！🎉 您已加入 MyDrAi 验血早鸟名单。',
    thanksBody: '服务推出后，我们会第一时间通过电子邮件或 WhatsApp 通知您。',
    backHome: '返回首页',
    redirecting: (n) => `${n} 秒后自动返回首页…`,
    footer: '无需付款，不发送垃圾邮件。准备好后，我们才会通知您。',
    err: {
      firstName: '请填写您的姓名。',
      email: '请填写有效的电子邮箱地址。',
      phone: '请填写有效的电话号码。',
      country: '请选择您的国家/地区。',
      postcode: (iso) => (iso === 'MY' ? '请填写有效的 5 位数邮区编号。' : '请填写有效的邮编。'),
      ageGroup: '请选择您的年龄组别。',
      interests: '请至少选择一项感兴趣的内容。',
      timing: '请选择您预计进行验血的时间。',
      consent: '请勾选同意接收最新消息。',
      generic: '暂时无法加入，请稍后再试。',
    },
  },
};

// Builds an E.164-style number from the selected country code + the number
// as typed. A leading '+' means the user typed a full international number,
// which wins over the dropdown. Otherwise the trunk '0' is dropped
// (012… → +6012…), and for Malaysia a pasted '60…' prefix is tolerated.
function normalizePhone(raw, iso) {
  const s = String(raw || '').replace(/[\s\-().]/g, '');
  if (!s) return '';
  if (s.startsWith('+')) return s;
  let national = s.replace(/\D/g, '').replace(/^0+/, '');
  if (iso === 'MY' && /^60\d{8,}$/.test(national)) national = national.slice(2);
  return `+${dialCode(iso)}${national}`;
}

function isValidPhone(e164) {
  return /^\+\d{8,15}$/.test(e164);
}

function isValidPostcode(code, iso) {
  const v = code.trim();
  if (!v) return NO_POSTCODE_COUNTRIES.has(iso);
  if (iso === 'MY') return /^\d{5}$/.test(v);
  return /^[A-Za-z0-9][A-Za-z0-9 -]{1,9}$/.test(v);
}

const inputCls =
  'w-full rounded-full border border-ink-200 bg-white pl-11 pr-5 py-3 text-sm focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200';

function IconInput({ icon: Icon, ...props }) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-ink-400">
        <Icon size={16} />
      </span>
      <input {...props} className={inputCls} />
    </div>
  );
}

function CountryOptions({ locale, t, withDial }) {
  const { popular, rest } = React.useMemo(() => countryOptions(locale === 'zh' ? 'zh-Hans' : 'en'), [locale]);
  const label = (c) => `${flagEmoji(c.iso)} ${c.name}${withDial ? ` (+${c.dial})` : ''}`;
  return (
    <>
      <optgroup label={t.popular}>
        {popular.map((c) => <option key={c.iso} value={c.iso}>{label(c)}</option>)}
      </optgroup>
      <optgroup label={t.allCountries}>
        {rest.map((c) => <option key={c.iso} value={c.iso}>{label(c)}</option>)}
      </optgroup>
    </>
  );
}

// Compact "🇲🇾 +60 ▾" pill; a transparent native <select> sits on top so
// mobile users get the OS picker with full country names.
function DialCodeSelect({ value, onChange, locale, t }) {
  return (
    <div className="relative shrink-0">
      <div className="pointer-events-none flex h-full items-center gap-1.5 rounded-full border border-ink-200 bg-white pl-4 pr-3 py-3 text-sm text-ink-900">
        <span aria-hidden="true">{flagEmoji(value)}</span>
        <span>+{dialCode(value)}</span>
        <ChevronDown size={14} className="text-ink-400" />
      </div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={t.phoneCode}
        title={countryName(value, locale === 'zh' ? 'zh-Hans' : 'en')}
        className="absolute inset-0 w-full cursor-pointer opacity-0"
      >
        <CountryOptions locale={locale} t={t} withDial />
      </select>
    </div>
  );
}

function Choice({ type, name, checked, onChange, children }) {
  return (
    <label
      className={`flex items-start gap-2.5 rounded-xl border px-3 py-2.5 text-sm cursor-pointer transition-colors ${
        checked ? 'border-brand-500 bg-brand-50 text-ink-900' : 'border-ink-200 bg-white text-ink-700 hover:border-brand-300'
      }`}
    >
      <input
        type={type}
        name={name}
        checked={checked}
        onChange={onChange}
        className="mt-0.5 h-4 w-4 shrink-0 accent-brand-600"
      />
      <span className="leading-snug">{children}</span>
    </label>
  );
}

function Question({ title, hint, children }) {
  return (
    <fieldset className="text-left">
      <legend className="text-sm font-semibold text-ink-900">{title}</legend>
      {hint && <p className="mt-0.5 text-xs text-ink-500">{hint}</p>}
      <div className="mt-2">{children}</div>
    </fieldset>
  );
}

export default function EarlyAccessForm({
  locale = 'en',
  source = 'preventive_health',
  variant = 'hero',
  storageKey,
  onSubmitted,
}) {
  const t = COPY[locale] || COPY.en;
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    firstName: '',
    email: '',
    phone: '',
    phoneCountry: DEFAULT_COUNTRY,
    country: DEFAULT_COUNTRY,
    countryTouched: false,
    postcode: '',
    ageGroup: '',
    interests: [],
    interestOther: '',
    testTiming: '',
    marketingConsent: false,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // Until the user picks a country on step 2, follow the phone country code.
  const setPhoneCountry = (iso) =>
    setForm((f) => ({ ...f, phoneCountry: iso, ...(f.countryTouched ? {} : { country: iso }) }));
  const setCountry = (iso) =>
    setForm((f) => ({
      ...f,
      country: iso,
      countryTouched: true,
      postcode: iso === 'MY' ? f.postcode.replace(/\D/g, '').slice(0, 5) : f.postcode,
    }));
  const postcodeOptional = NO_POSTCODE_COUNTRIES.has(form.country);
  const toggleInterest = (key) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(key) ? f.interests.filter((i) => i !== key) : [...f.interests, key],
    }));

  function validateStep1() {
    if (!form.firstName.trim()) return t.err.firstName;
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return t.err.email;
    if (!form.phoneCountry || !isValidPhone(normalizePhone(form.phone, form.phoneCountry))) return t.err.phone;
    return '';
  }
  function validateStep2() {
    if (!form.country) return t.err.country;
    if (!isValidPostcode(form.postcode, form.country)) return t.err.postcode(form.country);
    if (!form.ageGroup) return t.err.ageGroup;
    if (!form.interests.length) return t.err.interests;
    if (!form.testTiming) return t.err.timing;
    if (!form.marketingConsent) return t.err.consent;
    return '';
  }

  function onNext(e) {
    e.preventDefault();
    const msg = validateStep1();
    setError(msg);
    if (!msg) setStep(2);
  }

  async function onSubmit(e) {
    e.preventDefault();
    const msg = validateStep2();
    setError(msg);
    if (msg) return;

    const payload = {
      firstName: form.firstName.trim(),
      email: form.email.trim(),
      phone: normalizePhone(form.phone, form.phoneCountry),
      phoneCountry: form.phoneCountry,
      country: form.country,
      postcode: form.postcode.trim(),
      ageGroup: form.ageGroup,
      interests: form.interests,
      interestOther: form.interests.includes('other') ? form.interestOther.trim() : '',
      testTiming: form.testTiming,
      marketingConsent: true,
      source,
    };

    setBusy(true);
    try {
      await api.post('/waitlist', payload);
      if (storageKey) {
        try {
          const raw = localStorage.getItem(storageKey);
          const list = raw ? JSON.parse(raw) : [];
          list.push({ email: payload.email, ts: new Date().toISOString(), variant });
          localStorage.setItem(storageKey, JSON.stringify(list));
        } catch { /* storage full — non-fatal */ }
      }
      onSubmitted?.();
      setStep(3);
    } catch (err) {
      const data = err?.response?.data;
      setError(data?.details?.[0]?.message || data?.message || t.err.generic);
    } finally {
      setBusy(false);
    }
  }

  const wrapperClass = variant === 'hero'
    ? 'mt-8 rounded-2xl bg-white text-ink-900 shadow-card p-5 sm:p-6'
    : 'rounded-2xl bg-white text-ink-900 shadow-card p-5 sm:p-6 border border-ink-100';
  const lang = locale === 'zh' ? 'zh-Hans' : undefined;

  if (step === 3) {
    return (
      <>
        <div lang={lang} className={wrapperClass}>
          <div className="flex items-start gap-3 text-left">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-500 text-white shrink-0">
              <CheckCircle2 size={18} />
            </span>
            <div className="text-sm text-ink-700">
              <strong className="text-ink-900">{t.thanksTitle}</strong>
              <p className="mt-1">{t.thanksBody}</p>
            </div>
          </div>
        </div>
        <SuccessModal t={t} lang={lang} name={form.firstName.trim()} />
      </>
    );
  }

  return (
    <div lang={lang} className={wrapperClass}>
      {variant === 'hero' && (
        <h3 className="text-center text-base sm:text-lg font-bold text-ink-900">{t.heading}</h3>
      )}
      <div className={`${variant === 'hero' ? 'mt-3' : ''} flex items-center justify-center gap-2`}>
        {[1, 2].map((n) => (
          <span
            key={n}
            className={`h-1.5 w-10 rounded-full ${step >= n ? 'bg-brand-600' : 'bg-ink-200'}`}
          />
        ))}
        <span className="ml-1 text-[11px] font-semibold text-ink-500">{t.step(step)}</span>
      </div>

      {step === 1 ? (
        <form onSubmit={onNext} className="mt-4 space-y-3" noValidate>
          <IconInput
            icon={User}
            type="text"
            autoComplete="given-name"
            value={form.firstName}
            onChange={(e) => set('firstName', e.target.value)}
            placeholder={t.firstName}
            aria-label={t.firstName}
            maxLength={80}
          />
          <IconInput
            icon={Mail}
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            placeholder={t.email}
            aria-label={t.email}
            maxLength={254}
          />
          <div className="text-left">
            <label htmlFor={`ea-phone-${variant}`} className="block mb-1.5 pl-4 text-xs font-semibold text-ink-700">
              {t.phone}
            </label>
            <div className="flex gap-2">
              <DialCodeSelect value={form.phoneCountry} onChange={setPhoneCountry} locale={locale} t={t} />
              <div className="flex-1 min-w-0">
                <IconInput
                  id={`ea-phone-${variant}`}
                  icon={MessageCircle}
                  type="tel"
                  autoComplete="tel-national"
                  inputMode="tel"
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                  placeholder={t.phonePh}
                  maxLength={20}
                />
              </div>
            </div>
          </div>
          <button
            type="submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold bg-brand-600 text-white hover:bg-brand-700 transition-colors"
          >
            {t.next} <ArrowRight size={16} />
          </button>
        </form>
      ) : (
        <form onSubmit={onSubmit} className="mt-4 space-y-5" noValidate>
          <p className="text-center text-sm font-bold text-brand-700">{t.step2Title}</p>

          <Question title={t.country}>
            <div className="relative">
              <select
                value={form.country}
                onChange={(e) => setCountry(e.target.value)}
                aria-label={t.country}
                autoComplete="country"
                className="w-full appearance-none rounded-full border border-ink-200 bg-white pl-5 pr-10 py-2.5 text-sm text-ink-900 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
              >
                <CountryOptions locale={locale} t={t} />
              </select>
              <ChevronDown size={16} className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-ink-400" />
            </div>
          </Question>

          <Question title={<>{t.postcode}{postcodeOptional && <span className="ml-1 font-normal text-ink-500">{t.optional}</span>}</>}>
            <input
              type="text"
              inputMode={form.country === 'MY' ? 'numeric' : 'text'}
              autoComplete="postal-code"
              value={form.postcode}
              onChange={(e) => set(
                'postcode',
                form.country === 'MY'
                  ? e.target.value.replace(/\D/g, '').slice(0, 5)
                  : e.target.value.replace(/[^A-Za-z0-9 -]/g, '').slice(0, 10).toUpperCase()
              )}
              placeholder={t.postcodePh(form.country)}
              aria-label={t.postcode}
              className="w-full sm:w-48 rounded-full border border-ink-200 bg-white px-5 py-2.5 text-sm focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
            />
          </Question>

          <Question title={t.ageGroup}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {AGE_GROUPS.map((k) => (
                <Choice
                  key={k}
                  type="radio"
                  name={`ageGroup-${variant}`}
                  checked={form.ageGroup === k}
                  onChange={() => set('ageGroup', k)}
                >
                  {t.ageLabels[k]}
                </Choice>
              ))}
            </div>
          </Question>

          <Question title={t.interests} hint={t.interestsHint}>
            <div className="grid sm:grid-cols-2 gap-2">
              {INTERESTS.map((k) => (
                <Choice
                  key={k}
                  type="checkbox"
                  checked={form.interests.includes(k)}
                  onChange={() => toggleInterest(k)}
                >
                  {t.interestLabels[k]}
                </Choice>
              ))}
            </div>
            {form.interests.includes('other') && (
              <input
                type="text"
                value={form.interestOther}
                onChange={(e) => set('interestOther', e.target.value)}
                placeholder={t.otherPh}
                maxLength={200}
                className="mt-2 w-full rounded-xl border border-ink-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
              />
            )}
          </Question>

          <Question title={t.timing}>
            <div className="grid sm:grid-cols-2 gap-2">
              {TEST_TIMINGS.map((k) => (
                <Choice
                  key={k}
                  type="radio"
                  name={`testTiming-${variant}`}
                  checked={form.testTiming === k}
                  onChange={() => set('testTiming', k)}
                >
                  {t.timingLabels[k]}
                </Choice>
              ))}
            </div>
          </Question>

          <label className="flex items-start gap-2.5 text-left text-xs text-ink-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.marketingConsent}
              onChange={(e) => set('marketingConsent', e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-brand-600"
            />
            <span className="leading-relaxed">{t.consent}</span>
          </label>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setError(''); setStep(1); }}
              disabled={busy}
              className="inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-3.5 text-sm font-semibold border border-ink-200 text-ink-700 hover:bg-ink-50 disabled:opacity-60 transition-colors"
            >
              <ArrowLeft size={16} /> {t.back}
            </button>
            <button
              type="submit"
              disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold uppercase tracking-wide bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-60 transition-colors"
            >
              {busy ? t.submitting : t.submit}
            </button>
          </div>
        </form>
      )}

      {error && <p className="mt-2 text-xs text-rose-600 text-center" role="alert">{error}</p>}
      <p className="mt-3 text-center text-[11px] text-ink-500">{t.footer}</p>
    </div>
  );
}

const REDIRECT_SECONDS = 10;

// Full-screen "you're in" popup. Portalled to <body> because the form sits
// inside <Reveal>, whose transform would otherwise trap position:fixed.
function SuccessModal({ t, lang, name }) {
  const navigate = useNavigate();
  const [left, setLeft] = useState(REDIRECT_SECONDS);

  const goHome = React.useCallback(() => {
    navigate('/');
    window.scrollTo(0, 0);
  }, [navigate]);

  useEffect(() => {
    if (left <= 0) { goHome(); return undefined; }
    const id = setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [left, goHome]);

  // Lock background scroll while the popup is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return createPortal(
    <div
      lang={lang}
      role="dialog"
      aria-modal="true"
      aria-labelledby="early-access-success-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-ink-900/70 backdrop-blur-sm px-4"
    >
      <div className="w-full max-w-md rounded-3xl bg-white p-7 sm:p-9 text-center shadow-2xl">
        <span className="mx-auto inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-50 text-brand-600">
          <CheckCircle2 size={36} strokeWidth={2} />
        </span>
        <h2 id="early-access-success-title" className="mt-5 text-xl sm:text-2xl font-bold text-ink-900 leading-snug">
          {t.thanksTitle}
        </h2>
        {name && <p className="mt-2 text-sm font-semibold text-brand-700">{name}</p>}
        <p className="mt-3 text-sm text-ink-600 leading-relaxed">{t.thanksBody}</p>

        <button
          type="button"
          onClick={goHome}
          autoFocus
          className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold bg-brand-600 text-white hover:bg-brand-700 transition-colors"
        >
          <Home size={16} /> {t.backHome}
        </button>

        <div className="mt-4 h-1 w-full overflow-hidden rounded-full bg-ink-100" aria-hidden="true">
          <div
            className="h-full bg-brand-500 transition-[width] duration-1000 ease-linear"
            style={{ width: `${((REDIRECT_SECONDS - left) / REDIRECT_SECONDS) * 100}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-ink-500" aria-live="polite">{t.redirecting(Math.max(left, 0))}</p>
      </div>
    </div>,
    document.body
  );
}
